import { computed, unref, type Ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import Report from "@/services/analytics/report";

export interface LinkedInMetricsParams {
  organizationId: string;
  bucketOptionId?: string;
  period?: string | null;
  startDate?: string;
  endDate?: string;
}

export function useLinkedInMetrics(
  params: Ref<LinkedInMetricsParams | null> | LinkedInMetricsParams | null
) {
  const queryKey = computed(() => {
    const p = unref(params);
    if (!p) return ["linkedinMetrics", "disabled"];
    return [
      "linkedinMetrics",
      p.organizationId,
      p.bucketOptionId || "all",
      p.period || "",
      p.startDate || "",
      p.endDate || "",
    ];
  });

  const enabled = computed(() => {
    const p = unref(params);
    return p !== null && p !== undefined && !!p.organizationId;
  });

  return useQuery({
    queryKey,
    queryFn: async () => {
      const p = unref(params);
      if (!p) throw new Error("Parameters not ready");

      const queryParams: Record<string, any> = { organizationId: p.organizationId };
      if (p.bucketOptionId) queryParams.bucketOptionId = p.bucketOptionId;
      if (p.period) {
        queryParams.period = p.period;
      } else if (p.startDate && p.endDate) {
        queryParams.startDate = p.startDate;
        queryParams.endDate = p.endDate;
      }

      const result = await Report.getLinkedInMetrics(queryParams);
      if (result.data?.status) return result.data.data;
      throw new Error(result.data?.msg || "Failed to load LinkedIn metrics");
    },
    enabled,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}
